/**
 * GRC5253 control ID constant AWS-specific.
 */
export const GRC5253_CONTROL_ID = 'grc5253';

/**
 * GRC5253 control ID constant SDO-specific.
 */
export const GRC5253_SDO_CONTROL_ID = 'grc5253-sdo';

/**
 * Constants for button types used in GRC5253 dual button navigation.
 */
export const BUTTON_TYPES = {
  AWS: 'AWS',
  STORES: 'STORES',
} as const;

/**
 * Constants for lease types used in GRC5253 data filtering.
 */
export const LEASE_TYPES = {
  AWS: 'AWS',
  SDO: 'SDO',
} as const;

/**
 * Lease type field name constant.
 */
export const LEASE_TYPE_FIELD = 'leaseType';

/**
 * Primary use field name constant.
 */
export const PRIMARY_USE_FIELD = 'primaryUse';

/**
 * Primary use values to exclude when displaying SDO (Stores) leases.
 * 3PL (Third Party Leases) are managed separately and should not
 * appear in the SDO lease view. This exclusion does not apply to AWS leases.
 */
export const SDO_EXCLUDED_PRIMARY_USE = ['3PL'];

/**
 * Checks if the current date (in local time) falls within any of the predefined GRC review cycles.
 * If the current date is within a review cycle, the corresponding cycle label along with its start and end dates are returned.
 * If no matching cycle is found, it returns `null`.
 *
 * @param leaseType - Optional lease type ('AWS' or 'SDO'). SDO leases have earlier end dates (8th instead of 14th).
 *                    Defaults to AWS cycle dates when not specified.
 * @returns {Object | null} - An object containing the review cycle label, start date, and end date if the current date is within any of the cycles.
 *                             If no matching cycle is found, it returns `null`.
 *                             The object structure is as follows:
 *                             {
 *                               label: string,   // The label of the GRC review cycle name.
 *                               startDate: Date, // The start date of the GRC review cycle.
 *                               endDate: Date    // The end date of the GRC review cycle.
 *                             }
 */
export function getGRCReviewCycleDetails(leaseType?: string | null): {
  label: string;
  startDate: Date;
  endDate: Date;
} | null {
  // AWS uses AWS_GRC_REVIEW_CYCLE_DETAILS (end dates on the 14th, no gaps between cycles).
  // SDO uses SDO_GRC_REVIEW_CYCLE_DETAILS (end dates on the 8th, with gaps from 9th-14th
  // where no cycle is active and the banner will not be shown).
  const cycleDetails =
    leaseType === LEASE_TYPES.SDO ? SDO_GRC_REVIEW_CYCLE_DETAILS : AWS_GRC_REVIEW_CYCLE_DETAILS;
  // SDO uses PST (America/Los_Angeles) for date comparison.
  // AWS uses local time (original behavior, unchanged).
  let currentDate: Date;
  if (leaseType === LEASE_TYPES.SDO) {
    const pstDateStr = new Date().toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' });
    currentDate = new Date(pstDateStr);
  } else {
    currentDate = new Date();
  }
  for (const grcReviewCycle in cycleDetails) {
    const { startDate, endDate } = cycleDetails[grcReviewCycle];

    // Compare the current date with the review cycle's start and end date
    if (currentDate >= startDate && currentDate <= endDate) {
      return {
        label: grcReviewCycle,
        startDate,
        endDate,
      };
    }
  }

  return null;
}

export const AWS_GRC_REVIEW_CYCLE_DETAILS: { [key: string]: { startDate: Date; endDate: Date } } =
  (() => {
    const currentYear = new Date().getFullYear();

    return {
      // Quarter 1
      'Quarter 1 - January GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 0, 15),
        endDate: new Date(currentYear, 1, 14),
      },
      'Quarter 1 - February GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 1, 15),
        endDate: new Date(currentYear, 2, 14),
      },
      'Quarter 1 - March GRC Quarterly Review Cycle Phase 1': {
        startDate: new Date(currentYear, 2, 15),
        endDate: new Date(currentYear, 2, 31),
      },
      'Quarter 1 - March GRC Quarterly Review Cycle Phase 2': {
        startDate: new Date(currentYear, 3, 1),
        endDate: new Date(currentYear, 3, 14),
      },

      // Quarter 2
      'Quarter 2 - April GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 3, 15),
        endDate: new Date(currentYear, 4, 14),
      },
      'Quarter 2 - May GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 4, 15),
        endDate: new Date(currentYear, 5, 14),
      },
      'Quarter 2 - June GRC Quarterly Review Cycle Phase 1': {
        startDate: new Date(currentYear, 5, 15),
        endDate: new Date(currentYear, 5, 30),
      },
      'Quarter 2 - June GRC Quarterly Review Cycle Phase 2': {
        startDate: new Date(currentYear, 6, 1),
        endDate: new Date(currentYear, 6, 14),
      },

      // Quarter 3
      'Quarter 3 - July GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 6, 15),
        endDate: new Date(currentYear, 7, 14),
      },
      'Quarter 3 - August GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 7, 15),
        endDate: new Date(currentYear, 8, 14),
      },
      'Quarter 3 - September GRC Quarterly Review Cycle Phase 1': {
        startDate: new Date(currentYear, 8, 15),
        endDate: new Date(currentYear, 8, 30),
      },
      'Quarter 3 - September GRC Quarterly Review Cycle Phase 2': {
        startDate: new Date(currentYear, 9, 1),
        endDate: new Date(currentYear, 9, 14),
      },

      // Quarter 4
      'Quarter 4 - October GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 9, 15),
        endDate: new Date(currentYear, 10, 14),
      },
      'Quarter 4 - November GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 10, 15),
        endDate: new Date(currentYear, 11, 14),
      },
      'Quarter 4 - December GRC Quarterly Review Cycle Phase 1': {
        startDate: new Date(currentYear, 11, 15),
        endDate: new Date(currentYear, 11, 31),
      },
      'Quarter 4 - December GRC Quarterly Review Cycle Phase 2': {
        startDate: new Date(currentYear, 0, 1),
        endDate: new Date(currentYear, 0, 14),
      },
    };
  })();

/**
 * SDO-specific GRC review cycle details. SDO leases have earlier end dates (8th)
 * compared to AWS leases (14th). Phase 1 end dates (end of quarter month) remain the same.
 */
export const SDO_GRC_REVIEW_CYCLE_DETAILS: { [key: string]: { startDate: Date; endDate: Date } } =
  (() => {
    const currentYear = new Date().getFullYear();

    return {
      // Quarter 1
      'Quarter 1 - January GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 0, 15),
        endDate: new Date(currentYear, 1, 8),
      },
      'Quarter 1 - February GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 1, 15),
        endDate: new Date(currentYear, 2, 8),
      },
      'Quarter 1 - March GRC Quarterly Review Cycle Phase 1': {
        startDate: new Date(currentYear, 2, 15),
        endDate: new Date(currentYear, 2, 31),
      },
      'Quarter 1 - March GRC Quarterly Review Cycle Phase 2': {
        startDate: new Date(currentYear, 3, 1),
        endDate: new Date(currentYear, 3, 8),
      },

      // Quarter 2
      'Quarter 2 - April GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 3, 15),
        endDate: new Date(currentYear, 4, 8),
      },
      'Quarter 2 - May GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 4, 15),
        endDate: new Date(currentYear, 5, 8),
      },
      'Quarter 2 - June GRC Quarterly Review Cycle Phase 1': {
        startDate: new Date(currentYear, 5, 15),
        endDate: new Date(currentYear, 5, 30),
      },
      'Quarter 2 - June GRC Quarterly Review Cycle Phase 2': {
        startDate: new Date(currentYear, 6, 1),
        endDate: new Date(currentYear, 6, 8),
      },

      // Quarter 3
      'Quarter 3 - July GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 6, 15),
        endDate: new Date(currentYear, 7, 8),
      },
      'Quarter 3 - August GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 7, 15),
        endDate: new Date(currentYear, 8, 8),
      },
      'Quarter 3 - September GRC Quarterly Review Cycle Phase 1': {
        startDate: new Date(currentYear, 8, 15),
        endDate: new Date(currentYear, 8, 30),
      },
      'Quarter 3 - September GRC Quarterly Review Cycle Phase 2': {
        startDate: new Date(currentYear, 9, 1),
        endDate: new Date(currentYear, 9, 8),
      },

      // Quarter 4
      'Quarter 4 - October GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 9, 15),
        endDate: new Date(currentYear, 10, 8),
      },
      'Quarter 4 - November GRC Monthly Review Cycle': {
        startDate: new Date(currentYear, 10, 15),
        endDate: new Date(currentYear, 11, 8),
      },
      'Quarter 4 - December GRC Quarterly Review Cycle Phase 1': {
        startDate: new Date(currentYear, 11, 15),
        endDate: new Date(currentYear, 11, 31),
      },
      'Quarter 4 - December GRC Quarterly Review Cycle Phase 2': {
        startDate: new Date(currentYear, 0, 1),
        endDate: new Date(currentYear, 0, 8),
      },
    };
  })();